import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import {
  getFirestore, doc, getDoc, setDoc, getDocs,
  collection, serverTimestamp, writeBatch, deleteDoc, query, where, updateDoc} from "firebase/firestore";
import { cache, quotaGuard, usageMeter } from "./cacheLayer";

const firebaseConfig = {
  apiKey: "AIzaSyDS_2KA4O7ye50xFcALa43vLWzxmMYjyKY",
  authDomain: "creator-commandos.firebaseapp.com",
  projectId: "creator-commandos",
  storageBucket: "creator-commandos.firebasestorage.app",
  messagingSenderId: "797688942395",
  appId: "1:797688942395:web:32d7570f729614ef1c311e"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();

// Hardcoded admin emails for bootstrap only
export const ADMIN_EMAILS = ["jcosta89@gmail.com", "cole.the.slaw@gmail.com"];

// ── Firestore helper with quota guard + metering ───────────────────────────────
async function fsRead(docRef, channelId = "global") {
  return quotaGuard.guard(async () => {
    const snap = await getDoc(docRef);
    usageMeter.record(channelId, "reads", 1);
    return snap;
  });
}

async function fsWrite(docRef, data, channelId = "global", opts = {}) {
  return quotaGuard.guard(async () => {
    await setDoc(docRef, data, opts);
    usageMeter.record(channelId, "writes", 1);
  });
}

async function fsCollection(collRef, channelId = "global") {
  return quotaGuard.guard(async () => {
    const snap = await getDocs(collRef);
    usageMeter.record(channelId, "reads", snap.docs.length || 1);
    return snap;
  });
}

// ── Role management ────────────────────────────────────────────────────────────

export const getUserRole = async (email) => {
  // Cache user roles for 10 minutes — they rarely change
  const cached = cache.get("role", email);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "userRoles", email));
  const data = snap.exists() ? snap.data() : null;
  if (data) cache.set("role", email, data, 10 * 60 * 1000);
  return data;
};

export const setUserRole = async (email, role, channelIds = [], displayName = "") => {
  const data = { email, role, channelIds, displayName, updatedAt: serverTimestamp() };
  await fsWrite(doc(db, "userRoles", email), data, "global", { merge: true });
  cache.invalidate("role", email);
};

export const getAllUserRoles = async () => {
  const snap = await fsCollection(collection(db, "userRoles"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteUserRole = async (email) => {
  await quotaGuard.guard(() => deleteDoc(doc(db, "userRoles", email)));
  cache.invalidate("role", email);
};

export const bootstrapAdminRole = async (email) => {
  if (!ADMIN_EMAILS.includes(email)) return;
  const existing = await getUserRole(email);
  if (!existing) {
    await fsWrite(doc(db, "userRoles", email), {
      email, role: "admin", channelIds: [],
      displayName: email.split("@")[0],
      createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
  }
};

// ── Channels ───────────────────────────────────────────────────────────────────


// ── Subscription & Credit Management ───────────────────────────────────────────

export const getUserSubscription = async (email) => {
  try {
    const snap = await fsRead(doc(db, "userRoles", email), "global");
    if (!snap.exists()) return null;
    return snap.data();
  } catch(e) {
    console.error("[CC] getUserSubscription error:", e.message);
    return null;
  }
};

export const deductAiCredit = async (email) => {
  try {
    const ref = doc(db, "userRoles", email);
    const snap = await fsRead(ref, "global");
    if (!snap.exists()) return { allowed: false, reason: "no_account" };
    const data = snap.data();
    const status = data.subscription_status;
    const isActive = status === "active" || status === "trialing"; // eslint-disable-line no-unused-vars
    const plan = data.plan || "free";
    const remaining = data.ai_credits_remaining ?? (plan === "free" ? 10 : 0);

    if (remaining <= 0) {
      return { allowed: false, reason: "no_credits", remaining: 0 };
    }

    // Deduct one credit
    await fsWrite(ref, { ai_credits_remaining: remaining - 1 }, "global", { merge: true });
    return { allowed: true, remaining: remaining - 1 };
  } catch(e) {
    console.error("[CC] deductAiCredit error:", e.message);
    // Fail open — don't block AI if deduction fails
    return { allowed: true, remaining: null };
  }
};

export const getAiCreditsRemaining = async (email) => {
  try {
    const snap = await fsRead(doc(db, "userRoles", email), "global");
    if (!snap.exists()) return 10; // free tier default
    const data = snap.data();
    return data.ai_credits_remaining ?? 25;
  } catch(e) {
    return 10;
  }
};


// ── Activity Feed ────────────────────────────────────────────────────────────────

export const logActivity = async (channelId, action) => {
  try {
    const ref = doc(db, "channels", channelId, "meta", "activityFeed");
    const snap = await fsRead(ref, channelId);
    const existing = snap.exists() ? (snap.data().entries || []) : [];
    const entry = {
      id: Date.now().toString(),
      ...action,
      ts: new Date().toISOString(),
    };
    // Keep last 50 entries
    const updated = [entry, ...existing].slice(0, 50);
    await fsWrite(ref, { entries: updated }, channelId, { merge: false });
  } catch(e) {
    console.warn("[CC] logActivity failed:", e.message);
  }
};

export const getActivityFeed = async (channelId) => {
  try {
    const snap = await fsRead(doc(db, "channels", channelId, "meta", "activityFeed"), channelId);
    if (!snap.exists()) return [];
    return snap.data().entries || [];
  } catch(e) {
    return [];
  }
};

export const getChannel = async (channelId) => {
  try {
    const snap = await fsRead(doc(db, "channels", channelId), channelId);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch(e) {
    console.error("[CC] getChannel error:", e.message);
    return null;
  }
};

export const getChannels = async () => {
  const cached = cache.get("channels");
  if (cached) return cached;
  const snap = await fsCollection(collection(db, "channels"));
  const channels = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.deleted);
  cache.set("channels", "", channels, 30 * 60 * 1000);
  return channels;
};

export const saveChannel = async (channel) => {
  await fsWrite(doc(db, "channels", channel.id), channel, channel.id, { merge: true });
  cache.invalidate("channels");
};

// ── Videos ─────────────────────────────────────────────────────────────────────
// Videos stored per-document (one per video) — this is unavoidable for large sets.
// But we cache the entire video list aggressively.

export const getVideos = async (channelId) => {
  const cached = cache.get("videos", channelId);
  if (cached) return cached;
  const snap = await fsCollection(collection(db, "channels", channelId, "videos"), channelId);
  const videos = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
  cache.set("videos", channelId, videos, 60 * 60 * 1000); // 1 hour TTL
  return videos;
};

export const saveVideos = async (channelId, videos) => {
  const batch = writeBatch(db);
  videos.forEach(v => {
    batch.set(doc(db, "channels", channelId, "videos", v.id), v, { merge: true });
  });
  await quotaGuard.guard(() => batch.commit());
  usageMeter.record(channelId, "writes", Math.ceil(videos.length / 500));
  cache.invalidate("videos", channelId);
};

// ── Consolidated tracking (all videos in ONE document) ─────────────────────────
// Old: 1 doc per video = 1,000 reads
// New: 1 doc for all = 1 read

export const getTracking = async (channelId) => {
  const cached = cache.get("tracking", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "trackingAll"), channelId);
  const data = snap.exists() ? (snap.data().tracking || {}) : {};
  cache.set("tracking", channelId, data, 30 * 60 * 1000);
  return data;
};

export const updateTracking = async (channelId, videoId, trackingData) => {
  const current = cache.get("tracking", channelId) || {};
  const updated = { ...current, [videoId]: trackingData };
  cache.set("tracking", channelId, updated, 30 * 60 * 1000);
  // Debounced write handled by WriteDebouncer in useChannel
  await fsWrite(
    doc(db, "channels", channelId, "meta", "trackingAll"),
    { tracking: updated, updatedAt: serverTimestamp() },
    channelId
  );
};

export const bulkUpdateTracking = async (channelId, trackingMap) => {
  const current = cache.get("tracking", channelId) || {};
  const updated = { ...current, ...trackingMap };
  cache.set("tracking", channelId, updated, 30 * 60 * 1000);
  await fsWrite(
    doc(db, "channels", channelId, "meta", "trackingAll"),
    { tracking: updated, updatedAt: serverTimestamp() },
    channelId
  );
};

// ── Consolidated clips (all videos in ONE document) ────────────────────────────

export const getClips = async (channelId) => {
  const cached = cache.get("clips", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "clipsAll"), channelId);
  const data = snap.exists() ? (snap.data().clips || {}) : {};
  cache.set("clips", channelId, data, 30 * 60 * 1000);
  return data;
};

export const updateClips = async (channelId, videoId, clips) => {
  const current = cache.get("clips", channelId) || {};
  const updated = { ...current, [videoId]: clips };
  cache.set("clips", channelId, updated, 30 * 60 * 1000);
  await fsWrite(
    doc(db, "channels", channelId, "meta", "clipsAll"),
    { clips: updated, updatedAt: serverTimestamp() },
    channelId
  );
};

// ── Consolidated types (all videos in ONE document) ────────────────────────────

export const getVideoTypes = async (channelId) => {
  const cached = cache.get("types", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "typesAll"), channelId);
  const data = snap.exists() ? (snap.data().types || {}) : {};
  cache.set("types", channelId, data, 60 * 60 * 1000);
  return data;
};

export const saveVideoType = async (channelId, videoId, typeData) => {
  const current = cache.get("types", channelId) || {};
  const updated = { ...current, [videoId]: { ...typeData, updatedAt: new Date().toISOString() } };
  cache.set("types", channelId, updated, 60 * 60 * 1000);
  await fsWrite(
    doc(db, "channels", channelId, "meta", "typesAll"),
    { types: updated, updatedAt: serverTimestamp() },
    channelId
  );
};



// ── Classification schema (per-channel custom types) ──────────────────────────

export const DEFAULT_TYPES = [
  { id: "episode",    label: "Episode",     short: "EP",    color: "#2B5EA7", bg: "rgba(43,94,167,0.15)",    isDefault: true,  playlistHints: ["episode","podcast","show","series"] },
  { id: "live-solo",  label: "Live Solo",   short: "LIVE",  color: "#D93025", bg: "rgba(217,48,37,0.15)",    isDefault: false, playlistHints: ["live","stream","livestream"] },
  { id: "live-group", label: "Live Group",  short: "LIVE+", color: "#C9A227", bg: "rgba(201,162,39,0.15)",   isDefault: false, playlistHints: ["live show","live group"] },
  { id: "interview",  label: "Interview",   short: "INTV",  color: "#16a34a", bg: "rgba(22,163,74,0.15)",    isDefault: false, playlistHints: ["interview","interviews","conversation","guest"] },
];

export const getClassificationSchema = async (channelId) => {
  const cached = cache.get("schema", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "classificationSchema"), channelId);
  const data = snap.exists() ? snap.data().types : DEFAULT_TYPES;
  cache.set("schema", channelId, data, 60 * 60 * 1000);
  return data;
};

export const saveClassificationSchema = async (channelId, types) => {
  await fsWrite(
    doc(db, "channels", channelId, "meta", "classificationSchema"),
    { types, updatedAt: serverTimestamp() },
    channelId
  );
  cache.set("schema", channelId, types, 60 * 60 * 1000);
  // Also invalidate video types since schema changed
  cache.invalidate("types", channelId);
};

// Suggest new types based on playlist names
export const suggestTypesFromPlaylists = (playlists, existingTypes) => {
  const suggestions = [];
  const existingIds = new Set(existingTypes.map(t => t.id));
  // eslint-disable-next-line no-unused-vars
  const existingHints = existingTypes.flatMap(t => t.playlistHints || []);

  const PRESET_SUGGESTIONS = [
    { id: "clip",      label: "Clip",       short: "CLIP",  color: "#8b5cf6", bg: "rgba(139,92,246,0.15)",  playlistHints: ["clip","clips","shorts","highlights"] },
    { id: "tutorial",  label: "Tutorial",   short: "TUT",   color: "#0891b2", bg: "rgba(8,145,178,0.15)",   playlistHints: ["tutorial","tutorials","how to","guide","tips"] },
    { id: "review",    label: "Review",     short: "REV",   color: "#ea580c", bg: "rgba(234,88,12,0.15)",   playlistHints: ["review","reviews","react","reaction"] },
    { id: "vlog",      label: "Vlog",       short: "VLOG",  color: "#db2777", bg: "rgba(219,39,119,0.15)",  playlistHints: ["vlog","vlogs","day in","behind"] },
    { id: "q-and-a",   label: "Q&A",        short: "Q&A",   color: "#65a30d", bg: "rgba(101,163,13,0.15)",  playlistHints: ["q&a","qa","questions","ama"] },
    { id: "debate",    label: "Debate",     short: "DEB",   color: "#dc2626", bg: "rgba(220,38,38,0.15)",   playlistHints: ["debate","debates","vs","versus"] },
    { id: "news",      label: "News",       short: "NEWS",  color: "#0284c7", bg: "rgba(2,132,199,0.15)",   playlistHints: ["news","update","updates","breaking"] },
    { id: "education", label: "Educational",short: "EDU",   color: "#7c3aed", bg: "rgba(124,58,237,0.15)", playlistHints: ["lesson","education","learn","course","class"] },
  ];

  playlists.forEach(playlist => {
    const titleLow = playlist.title.toLowerCase();
    PRESET_SUGGESTIONS.forEach(preset => {
      if (existingIds.has(preset.id)) return;
      const matches = preset.playlistHints.some(hint => titleLow.includes(hint));
      if (matches && !suggestions.find(s => s.id === preset.id)) {
        suggestions.push({
          ...preset,
          suggestedFrom: playlist.title,
        });
      }
    });
  });

  return suggestions;
};

// ── AI Suggestions (stored per-video in clipsAll meta doc) ────────────────────

export const saveAiSuggestions = async (channelId, videoId, suggestions) => {
  const current = cache.get("aisugs", channelId) || {};
  const updated = { ...current, [videoId]: { suggestions, savedAt: new Date().toISOString() } };
  cache.set("aisugs", channelId, updated, 60 * 60 * 1000);
  await fsWrite(
    doc(db, "channels", channelId, "meta", "aiSuggestionsAll"),
    { suggestions: updated, updatedAt: serverTimestamp() },
    channelId
  );
};

export const getAiSuggestions = async (channelId) => {
  const cached = cache.get("aisugs", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "aiSuggestionsAll"), channelId);
  const data = snap.exists() ? (snap.data().suggestions || {}) : {};
  cache.set("aisugs", channelId, data, 60 * 60 * 1000);
  return data;
};


// ── Playlist map (video → playlists membership) ────────────────────────────────

export const getPlaylistMap = async (channelId) => {
  const cached = cache.get("playlists", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "playlistMap"), channelId);
  const data = snap.exists() ? (snap.data().map || {}) : {};
  cache.set("playlists", channelId, data, 24 * 60 * 60 * 1000); // 24hr TTL - playlists change rarely
  return data;
};

export const savePlaylistMap = async (channelId, map) => {
  await fsWrite(
    doc(db, "channels", channelId, "meta", "playlistMap"),
    { map, updatedAt: serverTimestamp() },
    channelId
  );
  cache.set("playlists", channelId, map, 24 * 60 * 60 * 1000);
};

export const getPlaylists = async (channelId) => {
  const cached = cache.get("playlistList", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "playlists"), channelId);
  const data = snap.exists() ? (snap.data().playlists || []) : [];
  cache.set("playlistList", channelId, data, 24 * 60 * 60 * 1000);
  return data;
};

export const savePlaylists = async (channelId, playlists) => {
  await fsWrite(
    doc(db, "channels", channelId, "meta", "playlists"),
    { playlists, updatedAt: serverTimestamp() },
    channelId
  );
  cache.set("playlistList", channelId, playlists, 24 * 60 * 60 * 1000);
};

// ── Transcripts (stored per-video — can be large) ──────────────────────────────

export const getTranscripts = async (channelId) => {
  // Don't cache transcripts in localStorage — too large
  // Load lazily per-video instead (see getTranscript below)
  return {};
};

export const getTranscript = async (channelId, videoId) => {
  const cached = cache.get("tx", channelId + "_" + videoId);
  if (cached !== null) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "transcripts", videoId), channelId);
  const text = snap.exists() ? (snap.data().text || "") : "";
  cache.set("tx", channelId + "_" + videoId, text, 120 * 60 * 1000); // 2 hour TTL
  return text;
};

export const updateTranscript = async (channelId, videoId, text) => {
  await fsWrite(
    doc(db, "channels", channelId, "transcripts", videoId),
    { text, updatedAt: serverTimestamp() },
    channelId,
    { merge: true }
  );
  cache.set("tx", channelId + "_" + videoId, text, 120 * 60 * 1000);
};

// ── Comments (stored per-video, loaded on demand) ──────────────────────────────

export const saveComments = async (channelId, videoId, comments) => {
  await fsWrite(
    doc(db, "channels", channelId, "comments", videoId),
    { comments, fetchedAt: serverTimestamp() },
    channelId,
    { merge: true }
  );
};

export const getComments = async (channelId, videoId) => {
  const snap = await fsRead(doc(db, "channels", channelId, "comments", videoId), channelId);
  return snap.exists() ? (snap.data().comments || []) : [];
};

// ── Branding + refresh log (meta docs) ────────────────────────────────────────

export const getBranding = async (channelId) => {
  const cached = cache.get("branding", channelId);
  if (cached !== null) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "branding"), channelId);
  const text = snap.exists() ? (snap.data().text || "") : "";
  cache.set("branding", channelId, text, 60 * 60 * 1000);
  return text;
};

export const saveBranding = async (channelId, text) => {
  await fsWrite(
    doc(db, "channels", channelId, "meta", "branding"),
    { text, updatedAt: serverTimestamp() },
    channelId
  );
  cache.set("branding", channelId, text, 60 * 60 * 1000);
};

export const getRefreshLog = async (channelId) => {
  const cached = cache.get("refreshLog", channelId);
  if (cached) return cached;
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "refreshLog"), channelId);
  const data = snap.exists() ? snap.data() : {};
  cache.set("refreshLog", channelId, data, 15 * 60 * 1000);
  return data;
};

export const saveRefreshLog = async (channelId, data) => {
  await fsWrite(
    doc(db, "channels", channelId, "meta", "refreshLog"),
    { ...data, updatedAt: serverTimestamp() },
    channelId
  );
  cache.set("refreshLog", channelId, data, 15 * 60 * 1000);
};

// ── OAuth tokens ───────────────────────────────────────────────────────────────

export const saveOAuthToken = async (channelId, tokenData) => {
  await fsWrite(
    doc(db, "channels", channelId, "meta", "oauthToken"),
    { ...tokenData, savedAt: serverTimestamp() },
    channelId
  );
};

export const getOAuthToken = async (channelId) => {
  const snap = await fsRead(doc(db, "channels", channelId, "meta", "oauthToken"), channelId);
  if (!snap.exists()) return null;
  const data = snap.data();
  const savedAt = data.savedAt?.toDate?.() || new Date(data.savedAtMs || 0);
  const expiresAt = new Date(savedAt.getTime() + (data.expiresIn || 3600) * 1000);
  if (new Date() >= expiresAt) return null;
  return data;
};

// ── Quota tracking ─────────────────────────────────────────────────────────────


export const appendQuotaEntry = async (dateKey, entry) => {
  // Only log YouTube API quota — don't log Firestore reads here to avoid circular cost
  const ref = doc(db, "quota", dateKey);
  const snap = await fsRead(ref, "global");
  const entries = snap.exists() ? (snap.data().entries || []) : [];
  await fsWrite(ref, { entries: [...entries, entry], updatedAt: serverTimestamp() }, "global");
};

export const getQuotaLog = async (dateKey) => {
  const snap = await fsRead(doc(db, "quota", dateKey), "global");
  return snap.exists() ? (snap.data().entries || []) : [];
};

export const getQuotaHistory = async (days = 14) => {
  const history = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
    const snap = await fsRead(doc(db, "quota", key), "global");
    const entries = snap.exists() ? (snap.data().entries || []) : [];
    const total = entries.reduce((s, e) => s + (e.units || e.u || 0), 0);
    history.push({ date: key, total, entries: entries.length });
  }
  return history;
};


// ── Referral System ───────────────────────────────────────────────────────────

export const generateReferralCode = () => {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const getReferralCode = async (email) => {
  const snap = await fsRead(doc(db, 'userRoles', email));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (data.referral_code) return data.referral_code;
  const code = generateReferralCode();
  await updateDoc(doc(db, 'userRoles', email), { referral_code: code });
  cache.invalidate('role', email);
  return code;
};

export const processReferral = async (newUserEmail, referralCode) => {
  try {
    const snap = await getDocs(query(collection(db, 'userRoles'), where('referral_code', '==', referralCode)));
    if (snap.empty) return;
    const referrerEmail = snap.docs[0].id;
    if (referrerEmail === newUserEmail) return;
    await updateDoc(doc(db, 'userRoles', newUserEmail), {
      referred_by: referralCode,
      referred_by_email: referrerEmail,
    });
    cache.invalidate('role', newUserEmail);
  } catch (e) {
    console.error('[CC] processReferral error:', e.message);
  }
};

export const awardReferralCredits = async (referrerEmail, bonusCredits = 25) => {
  try {
    const snap = await fsRead(doc(db, 'userRoles', referrerEmail));
    if (!snap.exists()) return;
    const data = snap.data();
    await updateDoc(doc(db, 'userRoles', referrerEmail), {
      ai_credits_remaining: (data.ai_credits_remaining || 0) + bonusCredits,
      ai_credits_total: (data.ai_credits_total || 0) + bonusCredits,
      referral_count: (data.referral_count || 0) + 1,
    });
    cache.invalidate('role', referrerEmail);
  } catch (e) {
    console.error('[CC] awardReferralCredits error:', e.message);
  }
};
